/* Тренировка клиента — просмотр (прототип workout.html): выполненная или назначенная тренировка так,
   как её видит клиент, — не конструктор. Под каждым блоком — журнал подходов (что клиент записал
   в «Итог», каждый подход своей ячейкой: как в плане — ✓, недобор — ↓, больше — ↑), итог комплекса
   и чат блока: вопросы, ответы тренера, ответить — здесь же. «Редактировать» — конструктор на тот же
   день, стрелки листают тренировки клиента. Сюда ведут вопросы и результаты из ленты (#b<id блока>). */
import { trpc, useCatalog } from '@tg/app-core';
import { blockHas, blockRows, blockTypeLabel, dayHas, factSummary, fmtLabel, fmtNum, linePlan, norm, type Block, type Line, type Maxes, type SetFact, type WorkoutContent, blockNote } from '@tg/domain';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DatePicker } from '../calendar/Calendar';
import { DAYICON, ICON } from '../constructor/icons';
import { Svg } from '../constructor/Svg';
import { useClients } from '../lib/data';
import { addDays, dm, ini, today } from '../lib/format';
import { useTitleSetter } from '../shell/Shell';
import { Cav } from '../ui/Cav';
import { toast } from '../ui/Toast';

const DOW = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const MON = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const dlong = (d: string) => {
  const x = new Date(d + 'T00:00:00');
  return DOW[(x.getDay() + 6) % 7] + ', ' + x.getDate() + ' ' + MON[x.getMonth()];
};
const hm = (t: Date | string) => {
  const x = new Date(t);
  return String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
};
const ymd = (t: Date | string) => {
  const x = new Date(t);
  return x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
};
/** «сегодня, 19:40», «вчера, 21:05», «14.10, 20:15» */
const when = (t: Date | string) => {
  const d = ymd(t);
  const T = today();
  return (d === T ? 'сегодня' : d === addDays(T, -1) ? 'вчера' : dm(d)) + ', ' + hm(t);
};
const num = (s: string | null | undefined) => {
  const m = String(s || '').match(/^\s*(\d+(?:[.,]\d+)?)\s*$/);
  return m ? +m[1].replace(',', '.') : null;
};

type Msg = { id: string; scope: string; refId: string | null; author: string; text: string; createdAt: Date | string };
type Res = { blockId: string; lineId: string; fact: unknown; plan: unknown };

export function WorkoutView() {
  const [sp, setSp] = useSearchParams();
  const cid = sp.get('client') || '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('date') || '') ? sp.get('date')! : today();
  const who = { clientId: cid };
  const cat = useCatalog();
  const c = useClients().data?.find((x) => x.id === cid);
  const days = trpc.trainer.days.useQuery({ who, from: addDays(date, -90), to: addDays(date, 90) }, { enabled: !!cid });
  const res = trpc.trainer.results.useQuery({ clientId: cid, from: date, to: date }, { enabled: !!cid });
  const thread = trpc.trainer.thread.useQuery({ clientId: cid, date }, { enabled: !!cid });
  const maxes = trpc.trainer.maxesFor.useQuery(who, { enabled: !!cid });
  const card = trpc.trainer.client.useQuery({ id: cid }, { enabled: !!cid });
  const pm: Maxes = (maxes.data as Record<string, Maxes> | undefined)?.[cid] || {};

  const row = days.data?.find((x) => x.date === date);
  const content = (row?.content ?? null) as WorkoutContent | null;
  const blocks = (content?.blocks || []).filter(blockHas);
  const withWorkout = (d: string) => days.data?.some((x) => x.date === d && dayHas(x.content as WorkoutContent));
  const step = (dir: 1 | -1) => {
    for (let k = 1; k <= 90; k++) {
      const d = addDays(date, dir * k);
      if (withWorkout(d)) return d;
    }
    return null;
  };
  const prev = step(-1);
  const next = step(1);
  const go = (d: string | null) => {
    if (!d) return;
    setSp((p) => {
      p.set('date', d);
      return p;
    });
    scrollTo(0, 0);
  };

  /* Статус для тренера: выполнена · не отмечена · сегодня · запланирована; черновик — клиент не видит */
  const T = today();
  const doneDay = !!res.data?.completions.some((x) => x.blockId === '' && x.date === date);
  const draft = row?.status === 'draft';
  const st: [string, string, string] | null = !blocks.length
    ? null
    : draft
      ? ['draft', DAYICON.draft, 'Черновик — клиент не видит']
      : doneDay
        ? ['pub', DAYICON.pub, 'Выполнена']
        : date < T
          ? ['draft', DAYICON.draft, 'Не отмечена']
          : date === T
            ? ['plan', '', 'Сегодня']
            : ['plan', '', 'Запланирована'];

  /* Заголовок страницы: «Тренировка» и дата — выбор по нажатию */
  const setTitle = useTitleSetter();
  const [dpk, setDpk] = useState<HTMLElement | null>(null);
  useEffect(() => {
    document.title = 'Тренировка · Тренерграм';
    setTitle({
      title: 'Тренировка',
      extra: (
        <button className="ptd" id="ptd" title="Выбрать дату" onClick={(e) => setDpk(dpk ? null : e.currentTarget)}>
          {dlong(date)}
          <Svg src={ICON.chev} />
        </button>
      ),
    });
  }, [date, dpk, setTitle]);

  /* Пришли из ленты к конкретному блоку — к нему и прокручиваем, поле ответа в фокусе */
  const jumped = useRef('');
  useEffect(() => {
    const m = /^#b(.+)$/.exec(location.hash);
    if (!m || !blocks.length || jumped.current === date + m[1]) return;
    jumped.current = date + m[1];
    setTimeout(() => {
      const el = document.getElementById('b' + m[1]);
      el?.scrollIntoView({ block: 'start' });
      el?.querySelector<HTMLInputElement>('[data-rk]')?.focus({ preventScroll: true });
    }, 60);
  }, [blocks.length, date]);

  /* Новые 1ПМ, записанные в этот день */
  const pms = useMemo(() => {
    const mx = ((card.data as { maxes?: { key: string; kg: number; reps: number; date: string }[] } | undefined)?.maxes || []).filter((m) => m.reps === 1);
    return mx
      .filter((m) => m.date === date)
      .map((m) => {
        const before = mx.filter((x) => x.key === m.key && x.date < date).sort((a, b) => b.date.localeCompare(a.date))[0];
        return { key: m.key, kg: m.kg, prev: before?.kg ?? null };
      });
  }, [card.data, date]);

  const msgs = (thread.data || []) as Msg[];
  const results = (res.data?.results || []) as Res[];
  const dayMsgs = msgs.filter((m) => m.scope === 'day');

  if (!cid) return null;
  return (
    <div className="pagewrap">
      <section className="wk wvwk">
        <div className="wkh one">
          <Link className="cliSel" to={'/clients/' + cid} title="Карточка клиента">
            <Cav ini={ini(c?.name || '')} photo={c?.photo} />
            <span className="cl-t">
              <b>{c?.name || 'Клиент'}</b>
              <s>{c?.group?.name || 'Карточка клиента'}</s>
            </span>
          </Link>
          <div className="dates">
            <span className="wkn">
              <button disabled={!prev} title={prev ? 'Предыдущая тренировка · ' + dm(prev) : undefined} aria-label="Предыдущая тренировка" onClick={() => go(prev)}>
                <Svg src={ICON.back} />
              </button>
              <button disabled={!next} title={next ? 'Следующая тренировка · ' + dm(next) : undefined} aria-label="Следующая тренировка" onClick={() => go(next)}>
                <Svg src={ICON.arr} />
              </button>
            </span>
          </div>
        </div>
      </section>
      {pms.length > 0 && (
        <div className="wvpm">
          <Svg src={ICON.star} />
          <span>
            Новый 1ПМ:{' '}
            {pms.map((r, i) => (
              <span key={r.key}>
                {i ? ', ' : ''}
                <b>
                  {cat.ex(r.key)?.ru || r.key} {fmtNum(r.kg)} кг
                </b>
                {r.prev != null ? ` (было ${fmtNum(r.prev)})` : ''}
              </span>
            ))}
          </span>
        </div>
      )}
      <section className="doc wvdoc">
        <div className="doch">
          {st && (
            <span className={'dst ' + st[0]} title={st[2]}>
              {st[1] ? <Svg src={st[1]} /> : null}
              {st[2].split(' — ')[0]}
            </span>
          )}
          <b className="dtt">{blocks.length || content?.comp ? content?.title || 'Тренировка' : days.isLoading ? '' : 'Нет тренировки'}</b>
          <Link className="btn gh sm" to={`/constructor?client=${cid}&date=${date}`}>
            <Svg src={ICON.build} /> {blocks.length ? 'Редактировать' : 'Составить'}
          </Link>
        </div>
        {blocks.length ? (
          <div className="wv">
            {content?.message ? (
              <div className="bxmsg">
                <Svg src={ICON.chat} />
                <span>{content.message}</span>
              </div>
            ) : null}
            {blocks.map((b, bi) => (
              <BlockLog key={b.id} b={b} bi={bi} pm={pm} results={results.filter((r) => r.blockId === b.id)} talk={msgs.filter((m) => m.scope !== 'day' && blockOwns(b, m.refId))} cid={cid} date={date} name={c?.name || 'Клиент'} />
            ))}
            {dayMsgs.length > 0 && <DayChat talk={dayMsgs} cid={cid} date={date} name={c?.name || 'Клиент'} />}
          </div>
        ) : (
          !days.isLoading && <div className="wvempty">{content?.comp ? 'Соревнование — тренировка не расписана' : 'На этот день тренировки нет'}</div>
        )}
      </section>
      {dpk && <DatePicker anchor={dpk} value={date} onPick={(d) => (setDpk(null), go(d))} onClose={() => setDpk(null)} />}
    </div>
  );
}

/** Сообщение относится к блоку: к нему самому или к строке внутри */
const blockOwns = (b: Block, ref: string | null) => !!ref && (ref === b.id || (b.items || []).some((y) => y.id === ref || (y.t === 'set' && y.items.some((l) => l.id === ref))));

/** Подход относительно плана: lo — меньше веса или повторов, hi — больше */
const setCmp = (x: SetFact, kg: number | null, reps: number | null) => {
  const dw = kg != null && x.kg != null ? x.kg - kg : 0;
  const dr = reps != null && x.reps != null ? x.reps - reps : 0;
  return dw < 0 || dr < 0 ? 'lo' : dw > 0 || dr > 0 ? 'hi' : 'ok';
};

const CHAT_ICON = ICON.chat;

function BlockLog({ b, bi, pm, results, talk, cid, date, name }: { b: Block; bi: number; pm: Maxes; results: Res[]; talk: Msg[]; cid: string; date: string; name: string }) {
  const cat = useCatalog();
  const [chatOn, setChatOn] = useState(talk.length > 0);
  const tl = blockTypeLabel(b);
  const title = b.title || tl || 'Блок';
  const type = b.title ? [tl && norm(tl) !== norm(b.title) ? tl : '', b.fmt && !b.title.includes(fmtLabel(b.fmt)) ? fmtLabel(b.fmt) : ''].filter(Boolean).join(' · ') : '';
  const fres = b.fmt ? factSummary(null, results.find((r) => r.lineId === '')?.fact as Record<string, unknown>, b.fmt) : '';
  const segs = blockRows(b);
  const nex = segs.flat().filter((r) => r.k === 'line').length;

  const lineRow = (line: Line, rounds: number, sub: boolean) => {
    const plan = linePlan(line, rounds, pm, cat.ex);
    const r = results.find((x) => x.lineId === line.id);
    const fact = (r?.fact || null) as { sets?: SetFact[]; kg?: number } | null;
    /* Старая запись одним весом ({ kg }) — этот вес во всех подходах плана */
    const got: SetFact[] = fact?.sets ? fact.sets.filter((s) => s.done || s.kg != null || s.reps != null) : fact?.kg != null ? Array.from({ length: plan.sets }, () => ({ kg: fact.kg, reps: null, done: true })) : [];
    const pkg = plan.kg && plan.kg.hi == null ? plan.kg.lo : null;
    const preps = num(plan.reps);
    return (
      <div key={line.id} className={'wl' + (sub ? ' sub' : '')}>
        {plan.text}
        {got.length > 0 && (
          <ol className="wsets">
            {got.map((x, q) => {
              const cmp = setCmp(x, pkg, preps);
              return (
                <li key={q} className={cmp}>
                  <s>{q + 1}</s>
                  <span>
                    {x.kg != null ? fmtNum(x.kg) : '—'}
                    {x.reps != null ? ' × ' + x.reps : ''}
                  </span>
                  <i>{cmp === 'lo' ? '↓' : cmp === 'hi' ? '↑' : '✓'}</i>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    );
  };

  return (
    <details className="wb" id={'b' + b.id} open>
      <summary className="wbh">
        <span className="wbn">{bi + 1}</span>
        <b className="t1">{title}</b>
        {type ? <span className="t3">{type}</span> : null}
        <span className="sp"></span>
        {fres ? <span className="t2 ok">Итог {fres} ✓</span> : null}
        {!talk.length && (
          <button
            className="wnb"
            title="Написать клиенту к этому блоку"
            aria-label="Написать клиенту к этому блоку"
            onClick={(e) => {
              e.preventDefault();
              setChatOn(!chatOn);
              if (!chatOn) setTimeout(() => document.querySelector<HTMLInputElement>(`#tk${b.id} [data-rk]`)?.focus());
            }}
          >
            <Svg src={CHAT_ICON} />
          </button>
        )}
        <i className="wch" aria-hidden="true"></i>
      </summary>
      <div className="wbb">
        <details className="wsec" open>
          <summary>
            <b>Упражнения</b>
            {nex ? <span className="t3">{nex}</span> : null}
          </summary>
          <div className="wsc">
            {blockNote(b) ? (
              <p className="t3 wbnote" title="Комментарий к блоку">
                <Svg src={CHAT_ICON} />
                {blockNote(b)}
              </p>
            ) : null}
            <div className="wls">
              {segs.map((seg) =>
                seg.map((r, k) =>
                  r.k === 'line' ? (
                    lineRow(r.line, r.rounds, r.rounds > 1)
                  ) : (
                    <div key={'t' + k + r.text} className={'wl' + (r.k === 'head' ? ' ss' : '')}>
                      {r.text}
                    </div>
                  ),
                ),
              )}
            </div>
          </div>
        </details>
        <Chat id={'tk' + b.id} hidden={!chatOn && !talk.length} talk={talk} cid={cid} date={date} scope="block" refId={b.id} name={name} />
      </div>
    </details>
  );
}

/** Сообщения ко всей тренировке (не к блоку) — отдельной веткой после блоков */
function DayChat({ talk, cid, date, name }: { talk: Msg[]; cid: string; date: string; name: string }) {
  return (
    <details className="wb" open>
      <summary className="wbh">
        <b className="t1">Вся тренировка</b>
        <span className="sp"></span>
        <i className="wch" aria-hidden="true"></i>
      </summary>
      <div className="wbb">
        <Chat id="tkday" hidden={false} talk={talk} cid={cid} date={date} scope="day" refId={null} name={name} />
      </div>
    </details>
  );
}

/* Чат блока — как в мессенджере: клиент слева, тренер справа, поле внизу. Ждёт ответа — последнее слово за клиентом */
function Chat({ id, hidden, talk, cid, date, scope, refId, name }: { id: string; hidden: boolean; talk: Msg[]; cid: string; date: string; scope: 'block' | 'day'; refId: string | null; name: string }) {
  const [v, setV] = useState('');
  const utils = trpc.useUtils();
  const send = trpc.trainer.send.useMutation({
    onSuccess: () => {
      setV('');
      toast('Ответ отправлен');
      utils.trainer.thread.invalidate();
      utils.trainer.feed.invalidate();
      utils.trainer.dashboard.invalidate();
    },
    onError: (e) => toast(e.message || 'Не отправилось — попробуйте ещё раз'),
  });
  const wait = talk.length > 0 && talk[talk.length - 1].author === 'client';
  const first = name.split(' ')[0];
  const go = () => v.trim() && !send.isPending && send.mutate({ clientId: cid, date, scope, refId, text: v.trim() });
  return (
    <details className="wsec wchat" id={id} open hidden={hidden}>
      <summary>
        <b>Чат</b>
        {talk.length ? <span className="t3">{talk.length}</span> : null}
        {wait ? <i className="wst q">ждёт ответа</i> : null}
      </summary>
      <div className="chat">
        {talk.length > 0 && (
          <div className="cbs">
            {talk.map((m) => (
              <div key={m.id} className={'cb ' + (m.author === 'trainer' ? 'me' : 'them')}>
                <div className="cbm">
                  {m.author === 'trainer' ? 'Вы' : first} · {when(m.createdAt)}
                </div>
                <div className="cbt">{m.text}</div>
              </div>
            ))}
          </div>
        )}
        <div className="wrin">
          <input className="inp" data-rk={id} placeholder={wait ? 'Ответить' : 'Сообщение клиенту'} aria-label="Сообщение клиенту" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} />
          <button className="btn sm" disabled={!v.trim() || send.isPending} onClick={go}>
            Отправить
          </button>
        </div>
      </div>
    </details>
  );
}
